// client/src/components/templates/certificados/TemplateCertificadoForm.js
import React, { useState, useEffect, useRef } from 'react';
import { useMutateTemplateCertificado, useCertificadoPlaceholders } from '../../../hooks/useTemplateCertificado';
import { useCursos } from '../../../hooks/useCursos';
import { useMyAccountDetails } from '../../../hooks/useAccounts';
import useAlert from '../../../hooks/useAlert';
import { useAuth } from '../../../hooks/useAuth';
import { isMatrizAdmin as checkIsMatrizAdmin } from '../../../utils/permissions';
import { Save, XCircle, Loader2, Code, Eye, ClipboardCopy, ChevronDown, LayoutTemplate } from 'lucide-react';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import PlaceholdersPanel from '../PlaceholdersPanel';
import CertificadoCanvasEditor from './CertificadoCanvasEditor';

// Estado inicial do template
const initialState = {
    nome: '',
    descricao: '',
    conteudo: '<p>Certificamos que {{cliente.nome}} concluiu com sucesso o curso de {{curso.nome}}.</p>',
    cursoAssociado: '',
    ativo: true,
    isPadrao: false
};

// 🔹 Form principal
const TemplateCertificadoForm = ({ onCloseForm, current }) => {
    const { user } = useAuth();
    const { setAlert } = useAlert();
    const { addTemplateCertificado, updateTemplateCertificado, isPendingCertificado: isMutatingTemplate } = useMutateTemplateCertificado();
    const { data: accountData } = useMyAccountDetails();
    const { data: cursosData, isLoading: isLoadingCursos } = useCursos(accountData?._id, { limit: 1000, ativo: true });
    const cursos = cursosData?.data || [];
    const { data: placeholders, isLoading: isLoadingPlaceholders } = useCertificadoPlaceholders();

    const [template, setTemplate] = useState(initialState);
    const [viewMode, setViewMode] = useState('editor');
    const [showVariables, setShowVariables] = useState(false);
    const editorRef = useRef(null);

    const isMatrizAdmin = checkIsMatrizAdmin(user) && !user?.account && !user?.franquia;

    useEffect(() => { 
        if (current) {
            setTemplate({
                ...initialState,
                ...current,
                conteudo: current.conteudo || initialState.conteudo,
                cursoAssociado: current.cursoAssociado?.id || current.cursoAssociado || ''
            });
        } else {
            setTemplate(initialState);
        }
    }, [current]);

    const onChange = e => {
        const { name, value, type, checked } = e.target;
        setTemplate(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };
    
    const onEditorChange = (event, editor) => {
        setTemplate(prev => ({ ...prev, conteudo: editor.getData() }));
    };

    const onSubmit = async e => {
        e.preventDefault();
        
        if (!template.nome.trim()) {
            setAlert('O nome do template é obrigatório.', 'danger');
            return;
        }

        const payload = { ...template, cursoAssociado: template.cursoAssociado || null };
        if (!isMatrizAdmin) {
            delete payload.isPadrao;
        }

        const mutation = current 
            ? updateTemplateCertificado.mutateAsync({ id: current._id, templateData: payload })
            : addTemplateCertificado.mutateAsync(payload);

        try {
            await mutation;
            onCloseForm();
        } catch (error) {
            setAlert(error.response?.data?.error || 'Erro ao salvar template de certificado.', 'danger');
        }
    };
    
    const insertPlaceholder = text => {
        const editor = editorRef.current;
        if (editor) {
            editor.model.change(writer => {
                const insertPosition = editor.model.document.selection.getFirstPosition();
                writer.insertText(text, insertPosition);
            });
            editor.editing.view.focus();
            setShowVariables(false);
        } else {
            setAlert('Editor não está pronto. Tente novamente.', 'warning');
        }
    };

    const overallLoading = isLoadingCursos || isMutatingTemplate;

    return (
        <form onSubmit={onSubmit} noValidate>
                    <div className="form-header">
                        <h3 className="form-title">
                            {current ? `Editar Template: ${template.nome}` : 'Novo Template de Certificado'}
                        </h3>
                        <button type="button" onClick={onCloseForm} title="Fechar" className="form-close-button">
                            <XCircle size={24} />
                        </button>
                    </div>

                    <fieldset className="form-section">
                        <legend className="form-section-title">Informações Gerais</legend>
                        <div className="form-grid-container">
                            <div className="form-group">
                                <label htmlFor="nome">Nome do Template*</label>
                                <input type="text" id="nome" name="nome" value={template.nome} onChange={onChange} required className="form-input" />
                            </div>

                            <div className="form-group">
                                <label htmlFor="cursoAssociado">Curso Associado (Opcional)</label>
                                <select id="cursoAssociado" name="cursoAssociado" value={template.cursoAssociado || ''} onChange={onChange} className="form-input" disabled={isLoadingCursos}>
                                    <option value="">-- Nenhum (serve para qualquer curso) --</option>
                                    {cursos.map(c => (<option key={c.id} value={c.id}>{c.nome}</option>))}
                                </select>
                            </div>

                            <div className="form-group full-width">
                                <label htmlFor="descricao">Descrição</label>
                                <textarea id="descricao" name="descricao" value={template.descricao || ''} onChange={onChange} rows="2" className="form-input" />
                            </div>
                        </div>
                    </fieldset>

                    <fieldset className="form-section">
                        <legend className="form-section-title">Conteúdo do Certificado*</legend>
                        <div className="editor-toolbar">
                            <button type="button" onClick={() => setViewMode('editor')} className={`action-button ${viewMode === 'editor' ? 'action-button-primary' : 'action-button-secondary'}`}>
                                <Code size={16} className="icon" /> Editor Visual
                            </button>
                            <button type="button" onClick={() => setViewMode('canvas')} className={`action-button ${viewMode === 'canvas' ? 'action-button-primary' : 'action-button-secondary'}`}>
                                <LayoutTemplate size={16} className="icon" /> Canvas (Arrastar Variáveis / IA)
                            </button>
                            {viewMode === 'editor' && (
                                <button type="button" onClick={() => setShowVariables(!showVariables)} className="action-button action-button-secondary">
                                    <ClipboardCopy size={16} className="icon" /> Inserir Variável
                                    <ChevronDown size={16} className={`transition-transform ${showVariables ? 'rotate-180' : ''}`} />
                                </button>
                            )}
                            <button type="button" onClick={() => setViewMode(viewMode === 'preview' ? 'editor' : 'preview')} className="action-button action-button-secondary">
                                <Eye size={16} className="icon" />{viewMode === 'preview' ? 'Voltar ao Editor' : 'Preview'}
                            </button>
                            <button type="button" onClick={() => setViewMode(viewMode === 'source' ? 'editor' : 'source')} className="action-button action-button-secondary">
                                <Code size={16} className="icon" />{viewMode === 'source' ? 'Editor Visual' : 'Código HTML'}
                            </button>
                        </div>
                        {showVariables && viewMode === 'editor' && <PlaceholdersPanel placeholders={placeholders} isLoading={isLoadingPlaceholders} onInsert={insertPlaceholder} />}
                        <div className="editor-container">
                            {viewMode === 'editor' && (
                                <CKEditor editor={ClassicEditor} data={template.conteudo} onChange={onEditorChange} onReady={(editor) => (editorRef.current = editor)} key={current ? current._id : 'new-certificado'} />
                            )}
                            {viewMode === 'canvas' && (
                                <CertificadoCanvasEditor
                                    html={template.conteudo}
                                    onChange={(novoHtml) => setTemplate(prev => ({ ...prev, conteudo: novoHtml }))}
                                    placeholders={placeholders || []}
                                    isLoadingPlaceholders={isLoadingPlaceholders}
                                />
                            )}
                            {viewMode === 'preview' && <div className="editor-preview prose max-w-none" dangerouslySetInnerHTML={{ __html: template.conteudo }}></div>}
                            {viewMode === 'source' && <textarea name="conteudo" value={template.conteudo} onChange={onChange} rows="15" className="editor-source-textarea"></textarea>}
                        </div>
                    </fieldset>
                    
                    <fieldset className="form-section">
                        <legend className="form-section-title">Configurações</legend>
                        <div className="form-group-checkbox">
                            <input type="checkbox" name="ativo" id="certificado-ativo" checked={template.ativo} onChange={onChange} className="form-checkbox" />
                            <label htmlFor="certificado-ativo">Template ativo</label>
                        </div>
                        {isMatrizAdmin && (
                            <div className="form-group-checkbox mt-4">
                                <input type="checkbox" name="isPadrao" id="certificado-isPadrao" checked={template.isPadrao} onChange={onChange} className="form-checkbox" />
                                <label htmlFor="certificado-isPadrao">Template Padrão Global (visível para todas as contas/franquias)</label>
                            </div>
                        )}
                    </fieldset>

                    <div className="form-actions">
                        <button type="button" onClick={onCloseForm} className="action-button action-button-secondary">
                            <XCircle size={18} className="icon" />Cancelar
                        </button>
                        <button type="submit" disabled={overallLoading} className="action-button action-button-primary">
                            {overallLoading ? <Loader2 className="animate-spin icon" /> : <Save size={18} className="icon" />}
                            {current ? 'Atualizar Template' : 'Salvar Template'}
                        </button>
                    </div>
        </form>
    );
};

export default TemplateCertificadoForm;